import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Market, TaskStatus, TaskUpdateMarketInfo } from "tm-shared/types";

import { BottomSheet } from "@/components/BottomSheet";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import Meta from "@/components/Meta";
import { CommentSelectModal } from "@/components/Modals/CommentSelectModal";
import { SVScanButton } from "@/components/SVScanButton";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useCheckCommentOnReject } from "@/hooks/useCheckCommentOnReject";
import {
    useSubtaskConfigSelector,
    useTaskConfigSelector,
} from "@/hooks/useTaskSelectors";
import {
    mutateUpdateMarketInfoPhotos,
    patchUpdateMarketInfoEntity,
} from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";
import { isSubtaskCompleted } from "@/utils/business/isSubtaskCompleted";

import { BottomSheetContent } from "./components/BottomSheetContent";
import { StepButton } from "./components/StepButton";
import { StepsSection } from "./components/StepsSection";
import { SubtaskRow } from "./components/SubtaskRow";

interface UpdateMarketInfoPageProps {
    task: TaskUpdateMarketInfo;
    market: Market;
}

export const UpdateMarketInfoPage: FC<UpdateMarketInfoPageProps> = ({
    task,
    market,
}) => {
    const dispatch = useAppDispatch();
    const [modal, setModal] = useState<"comment" | null>(null);

    const { updateMarketInfo } = useAppSelector((s) => s.progress);

    const taskId = task.id;
    const marketId = market.id;

    const payloadMap = updateMarketInfo[taskId];

    const { activeItem: step } = useTaskConfigSelector({
        marketId,
        taskId,
    });

    const currentStep = useMemo(() => {
        return isNaN(+step) ? 0 : +step;
    }, [step]);

    const subtasks = useMemo(() => {
        return task?.context?.subtasks ?? [];
    }, [task?.context?.subtasks]);

    const currentSubtask = useMemo(() => {
        return subtasks[currentStep];
    }, [subtasks, currentStep]);

    const subtaskId = currentSubtask?.subtaskId ?? "";
    const contractId = currentSubtask?.contractId ?? null;

    const { activeItem } = useSubtaskConfigSelector({
        marketId,
        taskId,
        subtaskId,
    });

    const activePayload = payloadMap?.[activeItem];
    const activeStatus = activePayload?.status ?? TaskStatus.Incomplete;
    const savedPhotos = activePayload?.photos ?? [];

    const subtaskTitle = currentSubtask?.name || "Оновлення інформації про ТТ";

    const prevActiveItemRef = useRef<string | null>(null);

    const { validateAndNotify } = useCheckCommentOnReject();

    const resetStatusIfNeeded = useCallback(
        (itemId: string) => {
            const payload = payloadMap?.[itemId];

            if (!payload) return;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                dispatch(
                    patchUpdateMarketInfoEntity({
                        taskId,
                        contractId,
                        subtaskId,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [taskId, contractId, subtaskId, payloadMap, dispatch],
    );

    const onCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                taskId,
                marketId,
                subtaskId,
                field: "activeItem",
                value: null,
            }),
        );
    }, [dispatch, taskId, marketId, subtaskId]);

    const handleStepChange = useCallback(
        (stepKey: number) => {
            dispatch(
                setMdTasksConfig({
                    marketId,
                    taskId,
                    field: "activeItem",
                    value: String(stepKey),
                }),
            );
        },
        [dispatch, taskId, marketId],
    );

    const handleChangeActive = useCallback(
        (id: string) => {
            if (id === activeItem) return;

            const currentId = prevActiveItemRef.current;
            const currentPayload = payloadMap?.[currentId];

            if (validateAndNotify(currentPayload)) {
                return;
            }

            dispatch(
                setMdTasksConfig({
                    taskId,
                    marketId,
                    subtaskId,
                    field: "activeItem",
                    value: id,
                }),
            );
        },
        [
            dispatch,
            marketId,
            taskId,
            subtaskId,
            activeItem,
            payloadMap,
            validateAndNotify,
        ],
    );

    const handleChangeComment = useCallback(
        (newComment: string) => {
            dispatch(
                patchUpdateMarketInfoEntity({
                    taskId,
                    subtaskId,
                    contractId,
                    patch: { comment: newComment },
                }),
            );
        },
        [dispatch, taskId, subtaskId, contractId],
    );

    const onChangeTaskStatus = useCallback(
        (value: TaskStatus, id?: string) => {
            const target = activeItem ?? id;

            if (!target) return;

            dispatch(
                patchUpdateMarketInfoEntity({
                    taskId,
                    contractId,
                    subtaskId: target,
                    patch: { status: value, comment: "" },
                }),
            );
        },
        [dispatch, taskId, contractId, activeItem],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            if (!activeItem) return;

            dispatch(
                mutateUpdateMarketInfoPhotos({
                    taskId,
                    subtaskId: activeItem,
                    contractId,
                    option: "add",
                    photo,
                }),
            );
        },
        [dispatch, taskId, contractId, activeItem],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            if (!activeItem) return;

            dispatch(
                mutateUpdateMarketInfoPhotos({
                    taskId,
                    subtaskId: activeItem,
                    contractId,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [dispatch, taskId, contractId, activeItem],
    );

    useEffect(() => {
        const prev = prevActiveItemRef.current;

        if (prev && activeItem !== prev) {
            resetStatusIfNeeded(prev);
        }

        prevActiveItemRef.current = activeItem ?? null;
    }, [activeItem, resetStatusIfNeeded]);

    return (
        <>
            <Meta title="Market info" />

            <Header
                title={task.name}
                back
                rightActions={
                    <>
                        <SVScanButton marketId={marketId} taskId={taskId} />
                    </>
                }
            />

            <MainContainer $padding="0">
                <StepsSection
                    subtasks={subtasks}
                    currentStep={currentStep}
                    onStepChange={handleStepChange}
                    payloadMapBySubtaskId={payloadMap}
                />

                <SubtaskRow
                    subtask={currentSubtask}
                    isActive={subtaskId === activeItem}
                    onChangeActive={handleChangeActive}
                    onChangeTaskStatus={onChangeTaskStatus}
                    payload={payloadMap?.[subtaskId]}
                />

                <StepButton
                    task={task}
                    marketId={marketId}
                    currentCompleted={isSubtaskCompleted(
                        payloadMap?.[subtaskId],
                    )}
                    currentStep={currentStep}
                />
            </MainContainer>

            <BottomSheet open={!!activeItem} onClose={onCloseBottomSheet}>
                <BottomSheetContent
                    title={subtaskTitle}
                    activeStatus={activeStatus}
                    savedPhotos={savedPhotos}
                    activeItemId={activeItem}
                    marketId={marketId}
                    captionInfo={{
                        market,
                        taskName: task.name,
                    }}
                    onChangeTaskStatus={onChangeTaskStatus}
                    onUploadPhoto={onUploadPhoto}
                    onDeletePhoto={onDeletePhoto}
                    onOpenCommentModal={() => setModal("comment")}
                />
            </BottomSheet>

            {modal === "comment" && (
                <CommentSelectModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={handleChangeComment}
                    initialComment={activePayload?.comment ?? ""}
                />
            )}
        </>
    );
};
